import React, { useMemo } from 'react';
import { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getPillStatusFromRating, parseTranslationWithPlaceholders } from 'commons/src/commonFunctions';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import StatusPill from 'commons/src/components/pills/StatusPill';
import { SensorStatusWithIcon } from 'commons/src/components/sensors/SensorStatusDot';
import { TRANSLATION_PLACEHOLDERS, sensorUnits } from 'commons/src/constants';
import { PillStatus, Rating, SensorCardMode, SensorTypes } from 'commons/src/models/commonEnums';
import { DayUsageHours } from 'commons/src/models/commonTypeScript';
import {
    SpaceMeasurement,
    formatRangeText,
    formatRating,
    formatThresholdNumber,
    getStatusColor,
    sensorRating,
    sensorShowsRatingLabels,
} from 'commons/src/sensorRatingUtils';
import { IaqInsightData, ReportSensorThresholds } from '../../../../../models/common';
import { Store } from '../../../../../reducers';
import IaqInsightGraph from '../../../iaqInsight/InsightGraph';
import styles from '../parts.module.scss';

export type SensorReportBaseProps = {
    sensor: SensorTypes;
    sensorData: IaqInsightData;
    sensorThreshold: ReportSensorThresholds;
    from: Moment;
    to: Moment;
    usageHours: { [day: string]: DayUsageHours };
    timeZone: string;
    hideCharts?: boolean;
};

type SummaryCardConfig = {
    title: string;
    value: number | undefined | null;
    rating: Rating | undefined;
    unit: string;
    hasValidValue: boolean;
    /** If true, show 0 instead of N/A. */
    showZeroAsValue?: boolean;
};

type DetailCardConfig = {
    rating: Rating;
    label: string;
    count: number;
    peakRange: { min: number; max: number } | null;
    unit: string;
};

type SpaceRow = {
    spaceId: string;
    name: string;
    floor?: number;
    roomType?: string;
    average?: number;
    lowestMeasurement?: number;
    highestMeasurement?: number;
    thresholds?: ReportSensorThresholds;
};

const SensorHeader: React.FC<{ sensor: SensorTypes; thresholds: ReportSensorThresholds }> = ({
    sensor,
    thresholds,
}) => {
    const { t: txt } = useTranslation();
    const sensorName = txt(sensor);

    return (
        <div className={styles.sensorHeader}>
            <div className={styles.sensorTitle}>
                <div className={styles.sensorIconWrapper}>
                    <SensorStatusWithIcon sensor={sensor} />
                </div>
                <h2 className={styles.sensorName}>{sensorName}</h2>
            </div>
            {sensorShowsRatingLabels(sensor) && (
                <div className={styles.ratingLegend}>
                    {thresholds.ranges
                        .map(range => {
                            const rangeText = formatRangeText(range, thresholds.unit);
                            return { range, rangeText };
                        })
                        .filter(({ rangeText }) => rangeText !== '')
                        .map(({ range, rangeText }) => {
                            const pillStatus = getPillStatusFromRating(range.rating) || PillStatus.success;
                            const rangeKey = `${range.rating}-${range.from ?? 'min'}-${range.to ?? 'max'}`;

                            return (
                                <div key={rangeKey} className={styles.legendItem}>
                                    <div className={styles.legendPillContainer}>
                                        <StatusPill status={pillStatus} size="small" outline />
                                        <span className={styles.legendRange}>{rangeText}</span>
                                    </div>
                                </div>
                            );
                        })}
                </div>
            )}
        </div>
    );
};

type SensorCardSummaryMode = {
    mode: SensorCardMode.Summary;
    title: string;
    value: number | undefined | null;
    unit: string;
    rating: Rating | undefined;
    hasValidValue: boolean;
    showZeroAsValue?: boolean;
};

type SensorCardDetailMode = {
    mode: SensorCardMode.Detail;
    count: number;
    label: string;
    rating: Rating;
    peakRange: { min: number; max: number } | null;
    unit: string;
    totalSpaces?: number;
    hasValidData?: boolean;
};

type SensorCardProps = SensorCardSummaryMode | SensorCardDetailMode;

const SensorCard: React.FC<SensorCardProps> = props => {
    const { t: txt } = useTranslation();
    const { mode } = props;

    const detailProps = mode === SensorCardMode.Detail ? props : null;
    const peakRangeText = useMemo(() => {
        if (detailProps === null || detailProps.peakRange === null) return null;
        const translation = txt('IAQReport2.HighestLevelReported');
        return parseTranslationWithPlaceholders(
            translation,
            {
                value: String(detailProps.peakRange.max),
                [TRANSLATION_PLACEHOLDERS.UNIT]: detailProps.unit,
            },
            new Set()
        );
    }, [txt, detailProps]);

    if (mode === SensorCardMode.Summary) {
        const { title, value, unit, rating, hasValidValue, showZeroAsValue } = props;
        const statusColor = rating ? getStatusColor(rating) : undefined;
        const showValue =
            hasValidValue &&
            value !== undefined &&
            value !== null &&
            !Number.isNaN(value) &&
            (value !== 0 || showZeroAsValue);

        return (
            <div className={styles.card}>
                <h4 className={styles.summaryCardTitle}>{title}</h4>
                <div className={styles.summaryCardValue}>
                    {showValue ? (
                        <>
                            <span className={styles.value}>{value}</span>
                            <span className={styles.unit}>{unit}</span>
                        </>
                    ) : (
                        <span className={styles.value}>{txt('IAQReport2.NotAvailable')}</span>
                    )}
                </div>
                {hasValidValue && rating && (
                    <div className={styles.summaryCardStatus}>
                        <span className={styles.statusText} style={{ color: statusColor }}>
                            {formatRating(rating)}
                        </span>
                    </div>
                )}
            </div>
        );
    }

    const { count, label, totalSpaces, hasValidData } = props;

    const renderCountText = (): React.ReactNode => {
        if (!hasValidData) {
            return <span className={styles.value}>{txt('IAQReport2.NotAvailable')}</span>;
        }
        if (totalSpaces !== undefined) {
            const text = txt('IAQReport2.SpacesOutOf', { count, total: totalSpaces });
            // Replace the first number with styled version
            const parts = text.split(/\b(\d+)\b/);
            return (
                <>
                    {parts.map((part, index) => {
                        if (index === 1) {
                            return (
                                <span key={`count-${part}`} className={styles.detailCountNumber}>
                                    {part}
                                </span>
                            );
                        }
                        // eslint-disable-next-line react/no-array-index-key
                        return <span key={`part-${part}-${index}`}>{part}</span>;
                    })}
                </>
            );
        }
        return count === 1 ? txt('IAQReport2.SpaceCount', { count }) : txt('IAQReport2.SpacesCount', { count });
    };

    return (
        <div className={styles.card}>
            <div className={styles.detailLabel}>{label}</div>
            <div className={styles.detailCardHeader}>
                <span className={styles.detailCount}>{renderCountText()}</span>
            </div>
            {peakRangeText && <div className={styles.detailPeakRange}>{peakRangeText}</div>}
        </div>
    );
};

const TableRowSensorStatus: React.FC<{
    value?: number;
    sensorRanges: ReportSensorThresholds;
    sensor: string;
}> = ({ sensor, value, sensorRanges: { ranges, unit } }) => {
    const valueRating = sensorRating(ranges, value);
    const displayedUnit: string =
        sensor === SensorTypes.temp ? `${sensorUnits[unit]}${(unit as string).toUpperCase()}` : sensorUnits[unit];
    if (value === undefined) {
        return <span>-</span>;
    }

    return (
        <div className={styles.sensorStatusContainer}>
            <div className={styles.sensorStatusRow}>
                <span className={styles.sensorValue}>{value}</span>
                <span>{displayedUnit}</span>
            </div>
            {sensorShowsRatingLabels(sensor) && valueRating && (
                <span className={styles[valueRating]}>{formatRating(valueRating)}</span>
            )}
        </div>
    );
};

const getDisplayUnit = (unitKey: string): string => {
    const baseUnit = sensorUnits[unitKey] || '';
    if (unitKey === 'c') {
        return `${baseUnit}C`;
    }
    if (unitKey === 'f') {
        return `${baseUnit}F`;
    }
    return baseUnit;
};

const formatThresholdDescription = (
    ranges: { from?: number; to?: number; rating: string }[],
    unitKey: string
): string => {
    const unit = getDisplayUnit(unitKey);
    const parts: string[] = [];
    ranges.forEach(range => {
        const ratingLabel = range.rating.charAt(0) + range.rating.slice(1).toLowerCase();
        if (range.to !== undefined && range.from === undefined) {
            parts.push(`${ratingLabel} below ${formatThresholdNumber(range.to)}${unit}`);
        } else if (range.from !== undefined && range.to !== undefined) {
            parts.push(
                `${ratingLabel} between ${formatThresholdNumber(range.from)} and ${formatThresholdNumber(
                    range.to
                )}${unit}`
            );
        } else if (range.from !== undefined && range.to === undefined) {
            parts.push(`${ratingLabel} above ${formatThresholdNumber(range.from)}${unit}`);
        }
    });
    return parts.join(', ');
};

const getThresholdKey = (thresholds: ReportSensorThresholds): string => {
    return JSON.stringify(thresholds.ranges.map(r => ({ from: r.from, to: r.to, rating: r.rating })));
};

const SpaceAirDataTable: React.FC<{
    sensorData: IaqInsightData;
    sensor: SensorTypes;
    sensorThreshold: ReportSensorThresholds;
    hoveredRating?: Rating | null;
}> = ({ sensorData, sensor, sensorThreshold, hoveredRating }) => {
    const { t: txt } = useTranslation();
    const devices = useSelector((state: Store) => state.devices.devicesWithKeyInfo);

    const rows: SpaceRow[] = Object.keys(
        sensorData.spaceOpeningHoursValues || sensorData.devicesOpeningHoursValues || {}
    )
        .map(spaceId => {
            const spaceData = (sensorData.spaceOpeningHoursValues || sensorData.devicesOpeningHoursValues)?.[spaceId];
            const device = devices[spaceId];
            const spaceName = spaceData?.name || device?.segmentName || spaceId;

            const row = {
                spaceId,
                name: spaceName,
                floor: spaceData?.floor,
                roomType: spaceData?.roomType,
                average: spaceData?.average,
                lowestMeasurement: spaceData?.lowestMeasurement,
                highestMeasurement: spaceData?.highestMeasurement,
                thresholds: spaceData?.thresholds,
            };

            return row;
        })
        .sort((a, b) => {
            if (a.floor !== undefined && b.floor !== undefined) {
                if (a.floor !== b.floor) {
                    return a.floor - b.floor;
                }
            }
            return a.name.localeCompare(b.name);
        });

    const uniqueThresholds = useMemo(() => {
        const thresholdMap = new Map<string, { thresholds: ReportSensorThresholds; spaceNames: string[] }>();
        rows.forEach(row => {
            if (row.thresholds) {
                const key = getThresholdKey(row.thresholds);
                const existing = thresholdMap.get(key);
                if (existing) {
                    existing.spaceNames.push(row.name);
                } else {
                    thresholdMap.set(key, { thresholds: row.thresholds, spaceNames: [row.name] });
                }
            }
        });
        return Array.from(thresholdMap.values());
    }, [rows]);

    const getFootnoteNumber = (thresholds: ReportSensorThresholds | undefined): number | null => {
        if (!thresholds) return null;
        const key = getThresholdKey(thresholds);
        const index = uniqueThresholds.findIndex(ut => getThresholdKey(ut.thresholds) === key);
        return index >= 0 ? index + 1 : null;
    };

    const hasNoData = (row: SpaceRow): boolean => {
        return (
            (row.average === undefined || row.average === null || Number.isNaN(row.average)) &&
            (row.lowestMeasurement === undefined || row.lowestMeasurement === null) &&
            (row.highestMeasurement === undefined || row.highestMeasurement === null)
        );
    };

    const spacesWithNoData = rows.filter(hasNoData);
    const superscriptNumbers = ['¹', '²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹'];

    const hasRowsWithData = rows.some(
        row =>
            (row.average !== undefined && row.average !== null && !Number.isNaN(row.average) && row.average !== 0) ||
            (row.lowestMeasurement !== undefined &&
                row.lowestMeasurement !== null &&
                !Number.isNaN(row.lowestMeasurement)) ||
            (row.highestMeasurement !== undefined &&
                row.highestMeasurement !== null &&
                !Number.isNaN(row.highestMeasurement))
    );

    if (!hasRowsWithData || rows.length === 0) {
        return null;
    }

    return (
        <div className={styles.tableContainer}>
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th className={styles.floorColumn}>{txt('IAQReport2.Floor')}</th>
                        <th className={styles.spaceColumn}>{txt('Space.SpaceName')}</th>
                        <th className={styles.valueColumn}>{txt('IAQReport2.AverageValue')}</th>
                        <th className={styles.valueColumn}>
                            {((): React.ReactNode => {
                                const text = txt('IAQReport2.LowestMeasurement');
                                const words = text.split(' ');
                                return words.length > 1
                                    ? ((
                                          <>
                                              {words[0]}
                                              <br />
                                              {words.slice(1).join(' ')}
                                          </>
                                      ) as React.ReactNode)
                                    : (text as React.ReactNode);
                            })()}
                        </th>
                        <th className={styles.valueColumn}>
                            {((): React.ReactNode => {
                                const text = txt('IAQReport2.HighestMeasurement');
                                const words = text.split(' ');
                                return words.length > 1
                                    ? ((
                                          <>
                                              {words[0]}
                                              <br />
                                              {words.slice(1).join(' ')}
                                          </>
                                      ) as React.ReactNode)
                                    : (text as React.ReactNode);
                            })()}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(row => {
                        const effectiveThresholds = row.thresholds || sensorThreshold;
                        const footnoteNum = getFootnoteNumber(row.thresholds);
                        const rowHasNoData = hasNoData(row);
                        const rowRating = sensorRating(effectiveThresholds.ranges, row.average);
                        const isHighlighted = hoveredRating != null && rowRating === hoveredRating;
                        const ratingHighlightClass: Partial<Record<Rating, string>> = {
                            [Rating.POOR]: styles.rowHighlightedPoor,
                            [Rating.FAIR]: styles.rowHighlightedFair,
                            [Rating.GOOD]: styles.rowHighlightedGood,
                        };
                        const highlightClass =
                            isHighlighted && hoveredRating ? ratingHighlightClass[hoveredRating] ?? '' : '';

                        return (
                            <tr key={row.spaceId} className={`${styles.row} ${highlightClass}`.trim()}>
                                <td className={styles.floorColumn}>{row.floor !== undefined ? row.floor : '-'}</td>
                                <td className={styles.spaceColumn}>
                                    <h2 className={styles.title}>
                                        {row.name}
                                        {rowHasNoData && <sup className={styles.footnoteMarker}>*</sup>}
                                        {footnoteNum && (
                                            <sup className={styles.footnoteMarker}>
                                                {superscriptNumbers[footnoteNum - 1] || footnoteNum}
                                            </sup>
                                        )}
                                    </h2>
                                </td>
                                <td className={styles.valueColumn}>
                                    <TableRowSensorStatus
                                        sensorRanges={effectiveThresholds}
                                        sensor={sensor}
                                        value={row.average}
                                    />
                                </td>
                                <td className={styles.valueColumn}>
                                    <TableRowSensorStatus
                                        sensorRanges={effectiveThresholds}
                                        sensor={sensor}
                                        value={row.lowestMeasurement}
                                    />
                                </td>
                                <td className={styles.valueColumn}>
                                    <TableRowSensorStatus
                                        sensorRanges={effectiveThresholds}
                                        sensor={sensor}
                                        value={row.highestMeasurement}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {(spacesWithNoData.length > 0 || uniqueThresholds.length > 0) && (
                <div className={styles.footnotes}>
                    {spacesWithNoData.length > 0 && (
                        <p className={styles.footnote}>
                            <span className={styles.footnoteMarker}>*</span>{' '}
                            {txt('IAQReport2.NoDataInSelectedTimePeriod')}
                        </p>
                    )}
                    {uniqueThresholds.map(ut => {
                        const description = formatThresholdDescription(
                            ut.thresholds.ranges,
                            String(ut.thresholds.unit)
                        );
                        return (
                            <p key={getThresholdKey(ut.thresholds)} className={styles.footnote}>
                                <sup>
                                    {superscriptNumbers[
                                        uniqueThresholds.findIndex(
                                            item => getThresholdKey(item.thresholds) === getThresholdKey(ut.thresholds)
                                        )
                                    ] ||
                                        uniqueThresholds.findIndex(
                                            item => getThresholdKey(item.thresholds) === getThresholdKey(ut.thresholds)
                                        ) + 1}
                                </sup>{' '}
                                {ut.spaceNames.join(', ')}: {txt('IAQReport2.CustomThresholdsSetTo')}{' '}
                                <span className={styles.thresholdDescriptionValue}>{description}</span>.
                            </p>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export const SensorReportBase: React.FC<SensorReportBaseProps> = ({
    sensor,
    sensorData,
    sensorThreshold,
    from,
    to,
    usageHours,
    timeZone,
    hideCharts = false,
}) => {
    const { t: txt } = useTranslation();

    if (!sensorData || !sensorThreshold) {
        return null;
    }

    const averageWithinHours = sensorData.averageValueWithinOpeningHours;
    const { averageValue } = sensorData;
    const averageRating = sensorRating(sensorThreshold.ranges, averageWithinHours);
    const overallRating = sensorRating(sensorThreshold.ranges, averageValue);

    const spaceValues: SpaceMeasurement[] = Object.values(
        sensorData.spaceOpeningHoursValues || sensorData.devicesOpeningHoursValues || {}
    );

    // Calculate spaces with poor overall average (based on average value)
    const spacesWithPoorAverage = spaceValues.filter(space => {
        if (space.average === undefined || space.average === null || Number.isNaN(space.average)) {
            return false;
        }
        const rating = sensorRating(sensorThreshold.ranges, space.average);
        return rating === Rating.POOR;
    }).length;

    // Calculate spaces with poor highest/lowest levels (either highest OR lowest is poor)
    const spacesWithPoorExtremes = spaceValues.filter(space => {
        let hasPoorExtreme = false;

        if (
            space.highestMeasurement !== undefined &&
            space.highestMeasurement !== null &&
            !Number.isNaN(space.highestMeasurement)
        ) {
            const highestRating = sensorRating(sensorThreshold.ranges, space.highestMeasurement);
            if (highestRating === Rating.POOR) {
                hasPoorExtreme = true;
            }
        }

        if (
            space.lowestMeasurement !== undefined &&
            space.lowestMeasurement !== null &&
            !Number.isNaN(space.lowestMeasurement)
        ) {
            const lowestRating = sensorRating(sensorThreshold.ranges, space.lowestMeasurement);
            if (lowestRating === Rating.POOR) {
                hasPoorExtreme = true;
            }
        }

        return hasPoorExtreme;
    }).length;

    const totalSpaces = spaceValues.length;

    const hasGraphData = sensorData.averageGraph && sensorData.averageGraph.length > 0;
    const hasSpacesWithData = spaceValues.some(
        spaceData =>
            spaceData.average !== undefined &&
            spaceData.average !== null &&
            !Number.isNaN(spaceData.average) &&
            spaceData.average !== 0
    );
    const hasValidChartData = hasGraphData || hasSpacesWithData;

    const unit = getDisplayUnit(String(sensorThreshold.unit));

    const hasValidAverageWithinHours =
        hasValidChartData &&
        averageWithinHours !== undefined &&
        averageWithinHours !== null &&
        !Number.isNaN(averageWithinHours) &&
        averageWithinHours !== 0;

    const hasValidAverageValue =
        hasValidChartData &&
        averageValue !== undefined &&
        averageValue !== null &&
        !Number.isNaN(averageValue) &&
        averageValue !== 0;

    const showRatingLabels = sensorShowsRatingLabels(sensor);
    const summaryCardConfigs: SummaryCardConfig[] = [
        {
            title: txt('IAQReport2.AverageWithinOpeningHours'),
            value: averageWithinHours,
            rating: showRatingLabels && hasValidAverageWithinHours ? averageRating : undefined,
            unit,
            hasValidValue: hasValidAverageWithinHours,
        },
        {
            title: txt('IAQReport2.AverageValueNoException'),
            value: averageValue,
            rating: showRatingLabels && hasValidAverageValue ? overallRating : undefined,
            unit,
            hasValidValue: hasValidAverageValue,
        },
    ];

    const detailCardConfigs: DetailCardConfig[] = [
        {
            rating: Rating.POOR,
            label: txt('IAQReport2.SpacesWithOverallPoorLevels'),
            count: spacesWithPoorAverage,
            peakRange: null,
            unit,
        },
        {
            rating: Rating.POOR,
            label: txt('IAQReport2.SpacesWithPoorHighestLowestLevels'),
            count: spacesWithPoorExtremes,
            peakRange: null,
            unit,
        },
    ];

    return (
        <div className={styles.sensorsView}>
            <SensorHeader sensor={sensor} thresholds={sensorThreshold} />
            <div className={styles.description}>{txt(`IAQReport2.${sensor}Description`)}</div>

            <div className={styles.summaryCards}>
                {summaryCardConfigs.map(config => (
                    <SensorCard
                        key={config.title}
                        mode={SensorCardMode.Summary}
                        title={config.title}
                        value={config.value}
                        unit={config.unit}
                        rating={config.rating}
                        hasValidValue={config.hasValidValue}
                        showZeroAsValue={config.showZeroAsValue}
                    />
                ))}
            </div>

            {showRatingLabels && (
                <div className={styles.detailCards}>
                    {detailCardConfigs.map(config => {
                        const cardProps = {
                            mode: SensorCardMode.Detail as const,
                            count: config.count,
                            label: config.label,
                            rating: config.rating,
                            peakRange: config.peakRange,
                            unit: config.unit,
                            totalSpaces,
                            hasValidData: hasValidChartData,
                        };
                        return (
                            <div
                                key={`${config.label}-${config.rating}-${config.count}`}
                                className={styles.detailCardHoverWrapper}
                                role="presentation"
                            >
                                <SensorCard {...cardProps} />
                            </div>
                        );
                    })}
                </div>
            )}
            {!hideCharts &&
                (hasValidChartData ? (
                    <>
                        <div className={styles.graphDescription}>{txt('IAQReport2.GraphDescription')}</div>
                        <div
                            id={`insightGraph-${sensor}`}
                            className="insight-tile__content insight-tile__content--graph"
                        >
                            <IaqInsightGraph
                                usageHours={usageHours}
                                chartData={sensorData.averageGraph || []}
                                ranges={sensorThreshold.ranges}
                                unit={sensorThreshold.unit}
                                sensor={sensor}
                                from={from}
                                to={to}
                                graphId={sensor}
                                timeZone={timeZone}
                            />
                        </div>
                    </>
                ) : (
                    <div className={styles.noDataContainer}>
                        <MaterialIcon name="info" extraClass={styles.noDataIcon} />
                        <div className={styles.noDataText}>{txt('IAQReport2.NoData')}</div>
                    </div>
                ))}
            <SpaceAirDataTable
                sensorData={sensorData}
                sensor={sensor}
                sensorThreshold={sensorThreshold}
                hoveredRating={null}
            />
        </div>
    );
};
